import React from 'react';
import { Box, Container, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ChurchIcon from '@mui/icons-material/Church';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { Link, Outlet } from 'react-router-dom';
import { STITCH } from '../../theme/stitchRegistrationTokens';
import { useBrand } from '../../brands/BrandProvider';

interface AuthLayoutProps {
  children?: React.ReactNode;
  /** Telas com formulário longo (ex.: cadastro público) precisam mais largura que login/recuperar senha */
  wideMain?: boolean;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ children, wideMain }) => {
  const brand = useBrand();
  const isCustomBrand = brand.key !== 'platform';
  const brandLogo = brand.logoUrl;
  const brandName = brand.displayName;
  const showTopBrand = true;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: wideMain ? STITCH.bg : (theme) => theme.palette.grey[100],
      }}
    >
      {wideMain ? (
        <Box
          component="header"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.appBar,
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 5 },
            bgcolor: STITCH.surfaceLowest,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            maxWidth: '100%',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            component={Link}
            to="/"
            sx={{ textDecoration: 'none', color: 'inherit' }}
          >
            {!isCustomBrand && (
              <ChurchIcon sx={{ color: STITCH.primary, fontSize: 28, display: { xs: 'block', sm: 'none' } }} aria-hidden />
            )}
            <Box
              component="img"
              src={brandLogo}
              alt={brandName}
              sx={{
                height: isCustomBrand ? { xs: 40, sm: 48 } : { xs: 36, sm: 40 },
                width: 'auto',
                maxWidth: isCustomBrand ? { xs: 160, sm: 200 } : { xs: 200, sm: 260 },
                objectFit: 'contain',
                display: { xs: isCustomBrand ? 'block' : 'none', sm: 'block' },
              }}
            />
            {!isCustomBrand && (
              <Typography
                sx={{
                  display: { xs: 'block', sm: 'none' },
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: STITCH.primary,
                  letterSpacing: '-0.01em',
                }}
              >
                {brandName}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Tooltip title="Página inicial">
              <IconButton component={Link} to="/" aria-label="Ir para a página inicial" sx={{ color: STITCH.onSurfaceVariant }}>
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Entrar no painel">
              <IconButton component={Link} to="/login" aria-label="Entrar" sx={{ color: STITCH.onSurfaceVariant }}>
                <AccountCircleOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      ) : null}

      <Container
        component="main"
        maxWidth={wideMain ? false : 'sm'}
        sx={{
          mb: 4,
          ...(wideMain
            ? {
                maxWidth: { xs: '100%', sm: 'min(100%, 960px)', md: 'min(100%, 1120px)', lg: 'min(100%, 1320px)' },
                px: { xs: 2, sm: 3, md: 5 },
                pt: '80px',
              }
            : {}),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: wideMain ? 'stretch' : 'center',
            mt: wideMain ? 0 : showTopBrand ? 8 : 6,
          }}
        >
          {!wideMain && showTopBrand ? (
            <Box
              component="header"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: { xs: 2, sm: 3 },
              }}
            >
              <Box
                component="img"
                src={brandLogo}
                alt={brandName}
                sx={{
                  height: { xs: 56, sm: 64 },
                  width: 'auto',
                  maxWidth: 'min(280px, 88vw)',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
              <Typography
                component="h1"
                sx={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  padding: 0,
                  margin: '-1px',
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}
              >
                {brandName}
              </Typography>
            </Box>
          ) : null}

          {children || <Outlet />}
        </Box>
      </Container>

      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: wideMain ? 'transparent' : (theme) => theme.palette.grey[200],
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="body2" color="text.secondary" align="center" sx={{ fontSize: wideMain ? '0.75rem' : undefined }}>
            {wideMain ? brand.copy.auth.footerWide : brand.copy.auth.footerShort}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default AuthLayout;
